import React from 'react'
import { Outlet } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { useMaterializedProcessesDataTable } from 'generated/resources/datatable.graphql'
import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'

import { observer } from 'mobx-react'

import DataTable from 'modules/core/components/DataTable'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import type { Process } from 'modules/processes/hooks/useProcesses'

import { AddEmployee } from './AddEmployee'
import { EmptyState } from './EmptyState'
import { filters as dataTableFilters } from './Filters'
import { projections } from './projectionts'

type Props = {
  process?: Process
  materializedTasksAccessId?: number
}

const MaterializedProcessesTable = ({ process, materializedTasksAccessId }: Props) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const isBoarding =
    typeof process?.category === 'string' &&
    ['offboarding', 'onboarding'].includes(process.category)
  const isTraining = process?.category === 'training'

  const variables = omitBy(
    {
      materializedTasksAccessId,
      processId: process?.id,
      includeItemsCount: true,
      search: debouncedSearch,
    },
    isNil
  )

  const source = useMaterializedProcessesDataTable(
    projections({
      processCategory: process?.category,
      isBoarding,
    }),
    {
      variables,
    }
  )

  const date = new Date()
  const formattedDate = date.toISOString().slice(0, 19).replace('T', '-').replace(/:/g, '-')
  const filename = `processes-${formattedDate}`

  return (
    <DataTable.Provider
      source={source}
      filters={dataTableFilters(source)}
      persistFilters={false}
      pagination={{
        kind: 'offset',
        itemsPerPage: 25,
        infiniteScroll: true,
      }}
      sort={{
        path: 'id',
        direction: 'desc',
      }}
    >
      <Box gap="s16">
        <Box height="full" flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <Text.Header2 weight="bold">{i18n.t('processes.process.employee.title')}</Text.Header2>
        </Box>
        <DataTable.HeadBar
          exportFileName={filename}
          search={{
            placeholder: i18n.t('common.search_by'),
            value: search,
            onChange: onSearch,
          }}
          actions={isTraining ? null : <AddEmployee />}
        />
        <DataTable.Table autoWidth emptyState={<EmptyState />} />
      </Box>
      <Outlet context={{ process: process }} />
    </DataTable.Provider>
  )
}

export default observer(MaterializedProcessesTable)
